import type { NotificationWorkflowItem } from '../../../shared/services/notification/workflow';
import { parseNumeric, postRequest } from '../../../shared/utils';

type Decision = 'ACCEPT' | 'REJECT';

type ResolveResult = {
  handled: boolean;
};

type SupportedRole = 'PP' | 'NCP' | 'CMMP' | 'QSFA';

const SUPPORTED_ROLES: ReadonlyArray<SupportedRole> = ['PP', 'NCP', 'CMMP', 'QSFA'];

function parseText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isSupportedRole(role: string): role is SupportedRole {
  return SUPPORTED_ROLES.includes(role as SupportedRole);
}

function normalizeRoleName(value: unknown): SupportedRole | null {
  const role = parseText(value)?.toUpperCase();

  if (!role || !isSupportedRole(role)) {
    return null;
  }

  return role;
}

function isNpiSteeringPayload(payload: Record<string, unknown>): boolean {
  const screen = parseText(payload.screen);
  return screen ? screen.toUpperCase() === 'NPI_STEERING' : true;
}

async function resolveDoneStateNewId(screenGridId: number): Promise<number | null> {
  const result = await postRequest('/api/records', {
    columns: ['id'],
    screenGridId,
    schema: 'npi',
    table: 'tblopt_done_state',
    key: 'state',
    value: 'New',
  });

  const rows = result?.data?.npi_tblopt_done_state;
  const [newRecord] = Array.isArray(rows) ? rows : [];

  return parseNumeric((newRecord as { id?: unknown })?.id);
}

function buildRoleSpecificSet({
  roleName,
  updatedBy,
  doneStateNewId,
}: {
  roleName: SupportedRole;
  updatedBy: number;
  doneStateNewId: number | null;
}) {
  if (doneStateNewId == null) {
    return null;
  }

  switch (roleName) {
    case 'PP':
      return {
        pruefplaner: updatedBy,
        pp_erledigt: doneStateNewId,
      };
    case 'NCP':
      return {
        nc_programmierer: updatedBy,
        ncperl: doneStateNewId,
      };
    case 'CMMP':
      return {
        cmmerl: doneStateNewId,
      };
    case 'QSFA':
      return {
        qsfaerl: doneStateNewId,
      };
    default:
      return null;
  }
}

function buildSheet1RoleDecisionPayload({
  rowId,
  updatedBy,
  roleName,
  doneStateNewId,
}: {
  rowId: number;
  updatedBy: number;
  roleName: SupportedRole;
  doneStateNewId: number | null;
}) {
  const roleSpecificSet = buildRoleSpecificSet({
    roleName,
    updatedBy,
    doneStateNewId,
  });

  if (!roleSpecificSet) {
    return null;
  }

  return {
    pkey: 'id1',
    schema: 'npi',
    table: 'sheet1',
    deleteIds: [],
    insertObjects: [],
    updateObjects: [
      {
        where: {
          id1: {
            _eq: rowId,
          },
        },
        _set: {
          ...roleSpecificSet,
          updated_by: updatedBy,
          update_date: new Date().toISOString().substring(0, 19),
        },
      },
    ],
  };
}

export async function resolveAvToDownstreamHandoffDecision({
  item,
  decision,
}: {
  item: NotificationWorkflowItem;
  decision: Decision;
}): Promise<ResolveResult> {
  const payload = (item.payload ?? {}) as Record<string, unknown>;

  if (!isNpiSteeringPayload(payload)) {
    return { handled: false };
  }

  const rowId = parseNumeric(payload.rowId);
  const screenGridId = parseNumeric(payload.screenGridId);
  const roleName = normalizeRoleName(payload.roleName);

  if (rowId == null || screenGridId == null || roleName == null) {
    return { handled: false };
  }

  if (decision === 'REJECT') {
    return { handled: true };
  }

  if (decision !== 'ACCEPT') {
    return { handled: false };
  }

  const updatedBy = parseNumeric(item.actingUserId);

  if (updatedBy == null) {
    return { handled: false };
  }

  const doneStateNewId = await resolveDoneStateNewId(screenGridId);

  if (doneStateNewId == null) {
    return { handled: false };
  }

  const tablePayload = buildSheet1RoleDecisionPayload({
    rowId,
    updatedBy,
    roleName,
    doneStateNewId,
  });

  if (!tablePayload) {
    return { handled: false };
  }

  const result = await postRequest('/api/tables', {
    screenGridId,
    tables: [tablePayload],
  });

  if (result?.errors?.length) {
    throw new Error(result.errors[0]?.message ?? 'Failed to execute AV to downstream handoff decision');
  }

  return { handled: true };
}
